import React, {memo, PureComponent, useState} from 'react';
import {useSearchParams} from 'react-router-dom';
import {observer} from 'mobx-react';
import I18n from 'app/i18n';
import SentTradeOffer from 'app/components/pages/trade_offer/sent_trade_offer';
import EmptyOffers from './empty_offers';
import InnerTabs from 'app/components/InnerTabs';
import ShowMore from 'app/utils/ShowMore';
import SmartLoader from 'app/components/reusable/SmartLoader';
import TradeInterface from 'app/interfaces/TradeInterface';
import {myTradesStore} from 'app/store/global';
import {
    getValidTabKey,
    TAB_CONFIG,
    TradeTabKey,
    TradeType
} from 'app/store/MyTradesStore';

interface MyTradesProps {
    empty_subtitle: string;
    onTabChange: (tab: TradeTabKey) => void;
    selectedTab: TradeTabKey;
    type: TradeType;
}

const renderTradeCount = (count: number | null): string => {
    if (count === null || count <= 0) {
        return '';
    }

    return ` (${count})`;
};

const tabs = (
    totalOutgoing: number | null,
    totalIncoming: number | null
): Array<{query: {type: TradeTabKey}; title: string}> => [
    {
        query: {type: 'outgoing'},
        title: `${I18n.t('user.sent')}${renderTradeCount(totalOutgoing)}`
    },
    {
        query: {type: 'incoming'},
        title: `${I18n.t('user.received')}${renderTradeCount(totalIncoming)}`
    },
    {
        query: {type: 'history'},
        title: I18n.t('user.history_header')
    }
];

@observer
class MyTrades extends PureComponent<MyTradesProps> {
    componentDidMount(): void {
        myTradesStore.ensureTabData(this.props.type);
    }

    componentDidUpdate(prevProps: MyTradesProps): void {
        if (prevProps.type !== this.props.type) {
            myTradesStore.ensureTabData(this.props.type);
        }
    }

    componentWillUnmount(): void {
        myTradesStore.reset();
    }

    acceptTrade = (id: number): void => {
        myTradesStore.acceptTrade(id, this.props.type);
    };

    cancelTrade = (id: number): void => {
        myTradesStore.cancelTrade(id, this.props.type);
    };

    loadTrades = (): Promise<void> => myTradesStore.loadTrades(this.props.type);

    render(): React.ReactNode {
        const {type, empty_subtitle, selectedTab, onTabChange} = this.props;
        const {contentLoading, total_outgoing, total_incoming} = myTradesStore;
        const {trades, hasMore, loaded} = myTradesStore.getTabState(type);
        const showLoader = contentLoading && !loaded;

        return <div className="exchange">
            <h1 className="title">{I18n.t('user.my_trades')}</h1>
            <div className="tabs">
                <div className="tabs__header trade">
                    <InnerTabs
                        handleChange={onTabChange}
                        queryParam="type"
                        selectedTab={selectedTab}
                        tabs={tabs(total_outgoing, total_incoming)}
                    />
                </div>
                <div className="tabs__content">
                    <div className="tabs__content-item">
                        {showLoader && <SmartLoader loaderClassName="trades-loader"/>}

                        {!showLoader && trades.length === 0 && <EmptyOffers
                            subtitle={empty_subtitle}
                            description={I18n.t('empty_trade_description')}
                        />}

                        {!showLoader && trades.map((trade: TradeInterface) => <SentTradeOffer
                            key={trade.id}
                            trade={trade}
                            type={type}
                            onCancelClick={this.cancelTrade}
                            onAcceptClick={this.acceptTrade}
                        />)}
                    </div>
                    <ShowMore onClickHandler={this.loadTrades} has_more={hasMore}/>
                </div>
            </div>
        </div>;
    }
}

const MyTradesPage = memo((): React.ReactElement => {
    const [searchParams] = useSearchParams();
    const [selectedTab, setSelectedTab] = useState<TradeTabKey>(
        getValidTabKey(searchParams.get('type'))
    );

    const handleChangeTab = (tab: TradeTabKey): void => {
        setSelectedTab(tab);
    };

    const {type, empty_subtitle_key} = TAB_CONFIG[selectedTab];

    return <MyTrades
        empty_subtitle={I18n.t(empty_subtitle_key)}
        onTabChange={handleChangeTab}
        selectedTab={selectedTab}
        type={type}
    />;
});

export default MyTradesPage;
